import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { SessionSourceType, WebSessionType } from 'src/api/learningSessions/types';
import { LoadingSkeleton } from 'src/apps/LearningSession/BlobSession/LoadingSkeleton';
import { useUserContext } from 'src/context/user';
import { useCurrentLanguagePair } from 'src/hooks/useCurrentLanguagePair';
import { useEligibility } from 'src/hooks/useEligibility';
import useSafePush from 'src/hooks/useSafePush';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { saveLastLanguagePair } from 'src/utils/language/saveLastLanguagePair';
import {
  clearBlobPronunciationDue,
  getBlobPronunciationQueueUrl,
  isBlobPronunciationDue,
  isBlobQueueSessionType,
  parseQueueId,
} from './queue';

export const BlobPronunciationQueue: FC<{
  children: ReactNode;
  sessionType: WebSessionType;
  sessionSourceType: SessionSourceType;
  sessionSourceId: number;
}> = ({ children, sessionType, sessionSourceType, sessionSourceId }) => {
  const { query } = useRouter();
  const { user } = useUserContext();
  const { safeReplace } = useSafePush();
  const isVocabularySession = isBlobQueueSessionType(sessionType);
  const languagePairId =
    sessionSourceType === 'language_pair'
      ? sessionSourceId
      : (parseQueueId(query.language_pair_id) ?? getLanguagePairId());
  const { eligibility, isLoading: eligibilityLoading } = useEligibility(
    isVocabularySession ? languagePairId : null,
  );
  const isBlob = !!eligibility?.eligibleForBlobExperience;
  const { languagePair, isLoading: languageLoading } = useCurrentLanguagePair(
    isVocabularySession && isBlob ? languagePairId : null,
  );
  const [canStartVocabulary, setCanStartVocabulary] = useState(false);
  const hasDecided = useRef(false);

  useEffect(() => {
    if (!isVocabularySession || !user || languagePairId === null || hasDecided.current) return;
    if (eligibilityLoading || (isBlob && languageLoading)) return;
    // Decide only on entry, before the vocabulary store mounts. A completion must
    // enqueue pronunciation for the next launch, not replace the current results.
    hasDecided.current = true;
    if (
      !isBlob ||
      languagePair?.supportsPronunciation !== true ||
      !isBlobPronunciationDue(user.id, languagePairId)
    ) {
      setCanStartVocabulary(true);
      return;
    }
    // Spend the due slot as soon as we offer pronunciation. Otherwise browser Back
    // from the intro leaves the counter at 2 and Learn keeps redirecting here.
    clearBlobPronunciationDue(user.id, languagePairId);
    saveLastLanguagePair(languagePairId);
    void safeReplace(
      getBlobPronunciationQueueUrl(languagePairId, parseQueueId(query.blob_id)),
    ).catch(() => setCanStartVocabulary(true));
  }, [
    eligibilityLoading,
    isBlob,
    isVocabularySession,
    languageLoading,
    languagePair?.supportsPronunciation,
    languagePairId,
    query.blob_id,
    safeReplace,
    user,
  ]);

  if (!isVocabularySession || !user || languagePairId === null || canStartVocabulary) {
    return <>{children}</>;
  }
  return <LoadingSkeleton title="" />;
};
